import { Add } from '@mui/icons-material';
import { Box, Button, Card, DialogActions, DialogContent, DialogTitle } from '@mui/material';
import { useMyAssortmentTFunction } from '@retail/my-assortment/i18n';
import { ContainedButton, Dialog, OutlinedButton } from '@shared/components';
import { Dispatch, SetStateAction, useCallback, useMemo, useState } from 'react';
import { AssortmentCard } from './AssortmentCard';
import { UnconnectedLocationsCard } from './UnconnectedLocationsCard';

interface Props {
  assortmentBindings: AssortmentBinding[];
  setAssortmentBindings: Dispatch<SetStateAction<AssortmentBinding[]>>;
  allLocations: LocationMinimal[];
  onSave: () => void;
  isSaving: boolean;
  onClose: () => void;
}

export interface LocationMinimal {
  id: number;
  name: string;
}
export interface AssortmentBinding {
  id: string;
  assortmentId?: number;
  title: string;
  locations: LocationMinimal[];
  isExisting?: boolean;
  isTouched?: boolean;
}

export function ConnectAssortmentsDialog({
  assortmentBindings,
  setAssortmentBindings,
  allLocations,
  onSave,
  isSaving,
  onClose,
}: Props) {
  const t = useMyAssortmentTFunction();

  const unconnectedLocations = useMemo(() => {
    const connectedLocationIds = new Set(
      assortmentBindings.flatMap((binding) => binding.locations.map((location) => location.id))
    );
    return allLocations.filter((location) => !connectedLocationIds.has(location.id));
  }, [allLocations, assortmentBindings]);

  const [checkedLocations, setCheckedLocations] = useState<LocationMinimal[]>([]);
  const handleToggleLocation = useCallback((location: LocationMinimal) => {
    setCheckedLocations((prevLocations) => {
      const locationIds = prevLocations.map((location) => location.id);
      if (locationIds.includes(location.id)) {
        return prevLocations.filter((prevLoc) => prevLoc.id !== location.id);
      } else {
        return [...prevLocations, location];
      }
    });
  }, []);

  // A *new* (editable) card whose name is blank or collides with any other card's
  // name locks the dialog: all other cards and the action buttons are disabled
  // until the name is fixed. Existing assortments are never flagged — their names
  // can't be changed, so two existing cards may legitimately share a name.
  const problemAssortmentId = useMemo(() => {
    const problem = assortmentBindings.find((binding) => {
      if (binding.isExisting) return false;
      const title = binding.title.trim().toLowerCase();
      if (title === '') return true;
      return assortmentBindings.some(
        (other) => other.id !== binding.id && other.title.trim().toLowerCase() === title
      );
    });
    return problem?.id;
  }, [assortmentBindings]);

  const isLocked = !!problemAssortmentId;

  const newAssortment = () =>
    setAssortmentBindings((prevBindings) => [
      ...prevBindings,
      { id: crypto.randomUUID(), title: '', locations: [] },
    ]);

  return (
    <Dialog open onClose={onClose} maxWidth="lg" fullWidth>
      <DialogTitle>{t('myAssortment.memberConfig.assortmentConnect')}</DialogTitle>
      <DialogContent>
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'row',
            alignItems: 'stretch',
            gap: 2,
            flexWrap: 'wrap',
          }}
        >
          {assortmentBindings.map((assortment, index) => {
            return (
              <AssortmentCard
                key={assortment.id}
                assortment={assortment}
                index={index}
                assortmentBindings={assortmentBindings}
                disabled={isLocked && assortment.id !== problemAssortmentId}
                setAssortmentBindings={setAssortmentBindings}
                checkedLocations={checkedLocations}
                handleToggleLocation={handleToggleLocation}
                resetCheckedLocations={() => setCheckedLocations([])}
              />
            );
          })}
          {unconnectedLocations.length > 0 && (
            <UnconnectedLocationsCard
              unconnectedLocations={unconnectedLocations}
              checkedLocations={checkedLocations}
              handleToggleLocation={handleToggleLocation}
              disabled={isLocked}
            />
          )}
          <Card
            component={Button}
            variant="outlined"
            startIcon={<Add />}
            onClick={newAssortment}
            disabled={isLocked}
            sx={{
              width: 'fit-content',
              py: 1,
              px: 2,
              borderStyle: 'dashed',
            }}
          >
            {t('myAssortment.memberConfig.addAssortment')}
          </Card>
        </Box>
      </DialogContent>
      <DialogActions>
        <OutlinedButton onClick={onClose} size="small" disabled={isSaving}>
          {t('myAssortment.cancel')}
        </OutlinedButton>
        <ContainedButton onClick={onSave} size="small" loading={isSaving} disabled={isLocked}>
          {t('myAssortment.save')}
        </ContainedButton>
      </DialogActions>
    </Dialog>
  );
}
